import { CurrentUserDTO, useGetCurrentUser } from "@bvhi/backend-api";
import { ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../hooks/use-toast";
import { toNarrowedTypeOrNull } from "../utils/ts";
import { AuthContext } from "./auth-context";

interface Props {
  children: ReactNode;
}

export interface BVHIAuthenticationBroadcastMessage {
  isAuthenticated: boolean;
}
function isInSecuredRoute() {
  if (window.location.href.includes("app")) return true;
  if (window.location.href.includes("profile")) return true;
  return false;
}

function convertToObjectIfPossible(item: unknown): object | undefined {
  if (typeof item === "object") return item!;

  if (typeof item === "string") {
    try {
      return JSON.parse(item) as object;
    } catch (error) {
      return undefined;
    }
  }
  return undefined;
}

function isValidCurrentUserResponse(data: object) {
  const objectData = convertToObjectIfPossible(data);
  if (!objectData) return false;
  return objectData && "firstName" in objectData && "lastName" in objectData && "id" in objectData;
}

function redirectToHome() {
  window.location.href = "/";
}

export function AuthContextProvider({ children }: Props) {
  const { pushCustomErrorToast } = useToast();
  const { t } = useTranslation(["toasts"]);

  const { data: currentUserResponse, refetch, isError } = useGetCurrentUser({ query: { staleTime: Infinity, retry: 0 } });

  useEffect(() => {
    if (currentUserResponse?.status === 401 && isInSecuredRoute()) {
      pushCustomErrorToast(t("toasts.user.unauthorized"));
      redirectToHome();
      return;
    }

    if (currentUserResponse?.status === 200 && !isValidCurrentUserResponse(currentUserResponse.data) && isInSecuredRoute()) {
      pushCustomErrorToast(t("toasts.user.unauthorized"));
      redirectToHome();
      return;
    }

    if (isError && isInSecuredRoute()) {
      pushCustomErrorToast(t("toasts.user.unauthorized"));
      redirectToHome();
    }
  }, [currentUserResponse, isError, pushCustomErrorToast, t]);

  const currentUser = toNarrowedTypeOrNull<CurrentUserDTO>(currentUserResponse?.data, "firstName") ?? undefined;
  return <AuthContext.Provider value={{ currentUser, refetch }}>{children}</AuthContext.Provider>;
}
